import { PRICE_TYPES } from 'shared/consts/productTemplates'

type GkAirProductWithAttributes = {
  attributes?: GenericRecord
  data?: GenericRecord
}

export type GkAirAreaData = {
  adServerMetadata?: GenericRecord
  content?: GkAirProductWithAttributes[]
  title?: string
}

export type GkAirRecommendationsResponse = {
  recommendations?: Record<string, GkAirAreaData>
}

export type GkAirMappedProduct = {
  canonicalURL: string
  darreichung: string
  discount: number | null
  gkAirAreaId: string
  gkAirContextPid: string | null
  gkAirTrackingToken: string | null
  id: string
  name: string
  numberOfRatings: number
  offerId: string
  offersCount: number
  prices: Array<{
    amount: number
    currency: string
    type: string
  }>
  rating: number
  sellerId: string
  sellerName: string
  shippingPrice: string
  thumbnailURL: string
  unit: string
  unitPricing: string | null
  unitQuantityString: string
}

type MappedCrossSellResponse = {
  end: number
  items: GkAirMappedProduct[]
  metadata: GenericRecord
  size: number
  start: number
  widgetdetails: {
    moreLinkTitle: string | null
    title: string | null
  }
}

const parseNumberOrNull = (input: unknown): number | null => {
  if (typeof input === 'number') {
    return Number.isFinite(input) ? input : null
  }

  if (typeof input !== 'string') {
    return null
  }

  const trimmed = input.trim()

  if (trimmed === '') {
    return null
  }

  const parsed = Number(trimmed.replace(',', '.'))

  return Number.isFinite(parsed) ? parsed : null
}

const parseIntegerOrZero = (input: unknown): number => {
  const parsed = parseInt(String(input), 10)

  return Number.isNaN(parsed) ? 0 : parsed
}

const getLocalizedField = (
  productData: GenericRecord,
  fieldPrefix: string,
  locale: string
): string => {
  const normalizedLocale = locale.replace('-', '_')
  const exactKey = `${fieldPrefix}_${normalizedLocale}`
  const exactValue = productData[exactKey]

  if (typeof exactValue === 'string') {
    return exactValue
  }

  const languagePrefix = `${fieldPrefix}_${normalizedLocale.split('_')[0]}_`
  const languageKey = Object.keys(productData).find(key =>
    key.startsWith(languagePrefix)
  )

  if (languageKey) {
    const languageValue = productData[languageKey]

    if (typeof languageValue === 'string') {
      return languageValue
    }
  }

  const fallbackKey = Object.keys(productData).find(key =>
    key.startsWith(`${fieldPrefix}_`)
  )

  if (fallbackKey) {
    const fallbackValue = productData[fallbackKey]

    if (typeof fallbackValue === 'string') {
      return fallbackValue
    }
  }

  return ''
}

const getUnitPricing = (
  productData: GenericRecord,
  locale: string,
  currency: string
): string | null => {
  const amount = parseNumberOrNull(productData.base_price_amount)
  const value = parseNumberOrNull(productData.base_price_value)
  const unit = getLocalizedField(productData, 'base_price_unit', locale)

  if (!amount || !value || !unit) {
    return null
  }

  return `${amount} ${currency} / ${value} ${unit}`
}

const mapGkAirProduct = ({
  areaId,
  contextPid,
  locale,
  product,
}: {
  areaId: string
  contextPid: string | null
  locale: string
  product: GkAirProductWithAttributes
}): GkAirMappedProduct | null => {
  const productData = product?.data || {}
  const productAttributes = product?.attributes || {}
  const id = String(productData.UID || '')
  const canonicalURL = getLocalizedField(productData, 'url', locale)
  const name = getLocalizedField(productData, 'name', locale)

  if (!id || !canonicalURL || !name) {
    return null
  }

  const currency = String(productData.base_price_currency || 'EUR')
  const retailAmount = parseNumberOrNull(productData.price)

  if (!retailAmount) {
    return null
  }

  const prices = [
    {
      amount: retailAmount,
      currency,
      type: PRICE_TYPES.RETAIL,
    },
  ]

  const listAmount = parseNumberOrNull(productData.strike_through_price)

  if (listAmount && listAmount >= retailAmount) {
    prices.unshift({
      amount: listAmount,
      currency,
      type: PRICE_TYPES.LIST,
    })
  }

  return {
    canonicalURL,
    darreichung: getLocalizedField(productData, 'pharma_form', locale),
    discount: parseNumberOrNull(productData.discount_in_percent),
    gkAirAreaId: areaId,
    gkAirContextPid: contextPid,
    gkAirTrackingToken:
      typeof productAttributes.trackingtoken === 'string'
        ? productAttributes.trackingtoken
        : null,
    id,
    name,
    numberOfRatings: parseIntegerOrZero(productData.rating_count),
    offerId: String(productData.offer_id || ''),
    offersCount: parseIntegerOrZero(productData.offers_count),
    prices,
    rating: parseNumberOrNull(productData.rating_average) || 0,
    sellerId: String(productData.seller_id || ''),
    sellerName: String(productData.seller_name || ''),
    shippingPrice: String(productData.offer_shipping_price || ''),
    thumbnailURL: getLocalizedField(productData, 'image_url', locale),
    unit: getLocalizedField(productData, 'package_size_unit', locale),
    unitPricing: getUnitPricing(productData, locale, currency),
    unitQuantityString: String(productData.package_size || ''),
  }
}

export const mapGkAirAreaToCrossSellResponse = ({
  areaData,
  areaId,
  contextPid,
  locale,
}: {
  areaData: GkAirAreaData
  areaId: string
  contextPid: string | null
  locale: string
}): MappedCrossSellResponse => {
  const items = (areaData?.content || [])
    .map(product =>
      mapGkAirProduct({
        areaId,
        contextPid,
        locale,
        product,
      })
    )
    .filter((item): item is GkAirMappedProduct => Boolean(item))

  return {
    end: items.length,
    items,
    metadata: areaData?.adServerMetadata || {},
    size: items.length,
    start: 0,
    widgetdetails: {
      moreLinkTitle: null,
      title: areaData?.title || null,
    },
  }
}
